import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import ConfirmationModal from "../../components/common/confirm/ConfirmationModal";
import AdminLoader from "../../components/common/loader/AdminLoader";
import { useDispatch, useSelector } from "react-redux";
import { updateTax, createTax, deleteTax, fetchTaxes } from "../../store/slices/taxSlice";
import { fetchEmployees } from "../../store/slices/employeeSlice";
import { setCurrentPageTitle } from "../../store/slices/pageSlice";
import { MdSearch } from "react-icons/md";
import { SquarePen, Trash2 } from "lucide-react";
import { CiCircleCheck } from "react-icons/ci";

const DEFAULT_TAXES = [
  { id: null, taxName: "G&A Expenses", taxPercentage: "15", isDefault: true },
  { id: null, taxName: "Tax", taxPercentage: "27", isDefault: true },
];

const Tax = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const email = localStorage.getItem("email");
  const { employees } = useSelector((state) => state.employees);
  const currentEmployee = employees?.find((emp) => emp.EmpEmail === email);

  useEffect(() => {
    dispatch(setCurrentPageTitle("Tax"));
  }, [dispatch]);

  const [isLoading] = useState(false);
  const [hardwareValue, SethardwareValue] = useState("");
  const [HardwareCost, SetHardwareCost] = useState("");
  const [hardwareData, SethardwareData] = useState([]);
  const [hardwareid, sethardwareid] = useState(null);
  const [isEditingDefault, setIsEditingDefault] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setdepartmenttoDelete] = useState(null);
  const [filteredtax, setfilteredtax] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const { taxes } = useSelector((state) => state.taxes);

  useEffect(() => {
    dispatch(fetchTaxes());
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Merge: defaults "G&A Expenses" and "Tax" always appear unless DB has a tax with that name (createdBy = current user). Then show DB entry only.
  useEffect(() => {
    const dbTaxes =
      currentEmployee != null
        ? (Array.isArray(taxes) ? taxes : []).filter(
            (tax) => tax.createdBy === currentEmployee._id
          )
        : [];
    const dbNamesLower = new Set(
      dbTaxes.map((t) => (t.taxName || "").trim().toLowerCase())
    );

    const merged = [];

    DEFAULT_TAXES.forEach((def) => {
      const nameLower = (def.taxName || "").trim().toLowerCase();
      if (!dbNamesLower.has(nameLower)) {
        merged.push({
          ...def,
          taxPercentage: String(def.taxPercentage ?? ""),
        });
      }
    });

    dbTaxes.forEach((tax) => {
      merged.push({
        id: tax.id ?? tax._id,
        taxName: tax.taxName,
        taxPercentage: String(tax.taxPercentage ?? ""),
        isDefault: false,
      });
    });

    SethardwareData(merged);
  }, [taxes, currentEmployee]);

  const handlePost = async () => {
    if (!hardwareValue?.trim() || !HardwareCost?.trim()) {
      enqueueSnackbar("Please fill all the fields!", { variant: "error" });
      return;
    }
    const newTax = {
      taxName: hardwareValue.trim(),
      taxPercentage: HardwareCost,
    };
    try {
      if (hardwareid != null && !isEditingDefault) {
        await dispatch(updateTax({ id: hardwareid, updatedData: newTax })).unwrap();
        enqueueSnackbar("Tax Updated Successfully!", { variant: "success" });
      } else {
        await dispatch(createTax(newTax)).unwrap();
        enqueueSnackbar("Tax Added Successfully!", { variant: "success" });
      }
      await dispatch(fetchTaxes());
    } catch (err) {
      enqueueSnackbar(err || "Something went wrong!", { variant: "error" });
    }
    SethardwareValue("");
    SetHardwareCost("");
    sethardwareid(null);
    setIsEditingDefault(false);
  };


  const handleDelete = (id) => {
    dispatch(deleteTax(id));
    dispatch(fetchTaxes());
  };




  const handleHardware = (e) => {
    const input = e.target.value;
    const onlyLetters = input.replace(/[^a-zA-Z\s]/g, "");
    SethardwareValue(onlyLetters);
  };

  const EditDepartment = (tax) => {
    const id = tax?.id ?? tax?._id;
    sethardwareid(id ?? null);
    setIsEditingDefault(Boolean(tax?.isDefault));
    SethardwareValue(tax?.taxName ?? "");

    SetHardwareCost(String(tax?.taxPercentage ?? ""));
  };

  const Cancel = (e) => {
    e.preventDefault();
    SethardwareValue("");
    SetHardwareCost("");
    sethardwareid(null);
    setIsEditingDefault(false);
  };




  const openDeleteModal = (id) => {
    if (id == null) return; // default rows: no delete from DB
    sethardwareid(id);
    setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setdepartmenttoDelete(null);
  };

  const confirmDelete = async () => {
    if (hardwareid) {
      await handleDelete(hardwareid); // Call your existing delete function
    }
    closeDeleteModal();
  };


  useEffect(() => {
    if (searchQuery.trim() === '') {
      setfilteredtax(hardwareData);
    } else {
      const filtered = hardwareData.filter(leadType =>
        leadType.taxName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setfilteredtax(filtered);
    }
  }, [searchQuery, hardwareData]);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

  };

  return (
    <>
      <>
        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
        />

        {isLoading ? (
          <AdminLoader isLoading={isLoading} />
        ) : (
          <div className="relative flex justify-center align-middle items-center pt-2">
            <div className="flex flex-col relative w-[80%] gap-4">
              {/* Page Title */}
              <div className="flex items-center gap-2">
                <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                <h1 className="text-2xl font-semibold">Add Tax</h1>
              </div>

              <div className="flex gap-2">
              <div className="w-[40%] bg-white rounded-md h-fit">
                <div className="flex px-3 py-4 gap-2 align-middle items-center border-b">
                  <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                  <h2 className="font-semibold text-xl">
                    Group Information
                  </h2>
                </div>
                <div className="mainform px-3 py-2 flex flex-col gap-3">
                  <div>
                    <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                      Tax Name <span className="text-red-700 text-xs">*</span>
                    </div>
                    <div>
                      <input
                        required
                        className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs"
                        type="text"
                        placeholder="Enter Tax Name"
                        value={hardwareValue}
                        onChange={handleHardware}
                      />
                    </div>
                  </div>
                  <div>
                    <div className='flex gap-1 pb-2 text-xs align-middle items-center font-semibold'>
                      % Tax Percentage
                    </div>
                    <div>
                      <input
                        className="border w-full bg-[#fcfcfc] border-[#e5e7eb] rounded-md p-2 overflow-hidden outline-none text-xs"
                        type="text"
                        placeholder="Enter Tax Percentage"
                        value={HardwareCost}
                        onChange={(e) => {
                          let newValue = e.target.value.replace(/[^0-9.]/g, ""); // Allow only numbers and a dot
                          newValue = newValue.replace(/(\..*)\./g, "$1"); // Prevent multiple dots

                          // Allow user to type freely, but restrict the final value to 1,000,000
                          if (newValue !== "" && parseFloat(newValue) > 1000000) {
                            return; // Stop updating if user tries to exceed 1 million
                          }

                          SetHardwareCost(newValue);
                        }}
                        onBlur={() => {
                          // Ensure value does not exceed 1 million after typing
                          if (parseFloat(HardwareCost) > 1000000) {
                            SetHardwareCost("1000000");
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={handlePost}
                      className="p-1 flex align-middle items-center gap-2 px-4 bg-[#0f61e6] hover:bg-blue-400 overflow-hidden text-white rounded-lg"
                    >
                      <CiCircleCheck size={16} />
                      {hardwareid ? "Update Tax" : "Add Tax"}
                    </button>
                    <button
                      onClick={Cancel}
                      className="py-2 px-3 border border-[#e5e7eb] bg-white overflow-hidden rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
              <div className="w-[60%] p-4 bg-white rounded-md">
                <div className="flex justify-between align-middle items-center py-2">
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
                    <h1 className="text-lg font-semibold text-gray-900">
                      Tax Directory
                    </h1>
                  </div>

                  <div className="flex gap-2 p-2 align-middle items-center border border-[#e5e7eb] rounded-lg">
                    <MdSearch className="w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      placeholder="Search Taxes"
                      className="w-full outline-none text-sm text-gray-900 placeholder-gray-500"
                    />
                  </div>
                </div>
                <hr />
                <hr />
                <br />
                <div className="maintable min-h-[10rem] max-h-[60vh] overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200 border text-xs">
                    <thead className="bg-[#e8e8e8] sticky top-0 z-50 font-semibold">
                      <tr>
                        <th className="border p-2 text-center tracking-wider font-medium capitalize">Sr.no.</th>
                        <th className="border p-2 text-start tracking-wider font-medium capitalize">Tax Name</th>
                        <th className="border p-2 text-start tracking-wider font-medium capitalize">Tax Percentage</th>
                        <th className="border p-2 text-center tracking-wider font-medium capitalize">Actions</th>
                      </tr>
                    </thead>

                    <tbody className='bg-white divide-y divide-gray-200'>
                      {filteredtax.length > 0 ? (
                        filteredtax.map((tax, index) => (
                          <tr className='border border-gray-400' key={index}>
                            <td className="px-2 border-b text-center py-2 text-blue-600">{index + 1}</td>
                            <td className="px-2 text-start border-b py-2">{tax.taxName}</td>
                            <td className="px-2 text-start border-b py-2">{tax.taxPercentage}%</td>
                            <td className="px-2 border-b text-center py-2">
                              <div className="flex justify-center items-center gap-3">
                                <div
                                  onClick={() => EditDepartment(tax)}
                                  className="cursor-pointer hover:opacity-70"
                                  title="Edit"
                                >
                                  <SquarePen size={18} className="text-blue-500" />
                                </div>
                                {!tax.isDefault && (
                                  <div
                                    onClick={() => openDeleteModal(tax.id ?? tax._id)}
                                    className="cursor-pointer hover:opacity-70"
                                    title="Delete"
                                  >
                                    <Trash2 size={18} className="text-blue-500" />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-red-500 py-4">
                            {searchQuery.trim() === ''
                              ? 'No tax available'
                              : 'No matching tax found'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </div>
          </div>
        )}
      </>


    </>
  )
}

export default Tax;